import { QueryClient, keepPreviousData } from '@tanstack/react-query';
import { isApiError } from '@/lib/apiError';
import { ErrorCode } from '@/types/api';

// -----------------------------------------------------------------------------
// TanStack Query defaults.
//
// The retry policy is the important part. Blind retries on an admin API are
// actively harmful: retrying a 403 spams the audit log with denied attempts, and
// retrying a 422 cannot succeed because the request itself is wrong. Only
// genuinely transient failures are retried.
//
// Mutations are NEVER retried. A PATCH that approves a coach may well have been
// applied before the response was lost - replaying it risks a second write, and
// on a destructive action a silent duplicate is worse than a visible failure the
// operator can decide about.
// -----------------------------------------------------------------------------

const NON_RETRYABLE = new Set<string>([
  ErrorCode.AUTH_REQUIRED,
  ErrorCode.AUTH_EXPIRED,
  ErrorCode.AUTH_INVALID,
  ErrorCode.APP_CHECK_FAILED,
  ErrorCode.FORBIDDEN,
  ErrorCode.ROLE_MISMATCH,
  ErrorCode.ACCOUNT_SUSPENDED,
  ErrorCode.ONBOARDING_INCOMPLETE,
  ErrorCode.VALIDATION_FAILED,
  ErrorCode.RESOURCE_NOT_FOUND,
  ErrorCode.RESOURCE_CONFLICT,
  ErrorCode.PAYLOAD_TOO_LARGE,
  ErrorCode.UNSUPPORTED_MEDIA_TYPE,
  ErrorCode.RATE_LIMITED,
  ErrorCode.CANCELLED,
]);

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry(failureCount, error) {
          if (isApiError(error) && NON_RETRYABLE.has(error.code)) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),

        // -- Freshness ---------------------------------------------------------
        //
        // This is the stale-while-revalidate contract, and it is the whole
        // reason navigating this panel feels instant.
        //
        // The mobile app's performance audit found the opposite arrangement:
        // a hook with no cache, so returning to an already-loaded tab showed a
        // full-screen spinner and refetched everything. Firestore sits in nam5
        // and a round trip from India measured ~350ms, so every one of those
        // refetches was a visible stall. The fix there was a keyed SWR cache;
        // here TanStack Query provides it, and these defaults are what turn it
        // on rather than leaving it at `staleTime: 0` (refetch on every mount).
        //
        // 30s: staff move between screens constantly, and admin data is read
        // far more than it changes. Long enough that a back-navigation issues
        // NO request at all; short enough that a moderation queue is never
        // meaningfully out of date. Screens that need tighter or looser
        // freshness override it per query - the analytics query uses 60s.
        staleTime: 30_000,

        // Survives a tab round-trip so the data is still there to paint from.
        gcTime: 10 * 60_000,

        // Revalidate behind the visible data, never instead of it.
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,

        // `always` would refetch on every mount and defeat everything above.
        refetchOnMount: true,

        // Keeps the PREVIOUS page's rows on screen while the next page or a
        // changed filter loads. Without this, typing in a search box or
        // switching a status filter blanks the table to a skeleton on every
        // keystroke - the table flashes, the scroll position resets, and the
        // screen feels slower than it is even though the request is identical.
        placeholderData: keepPreviousData,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * Query keys, centralised.
 *
 * Every key is a prefixable array so a mutation can invalidate a whole feature
 * with `queryKey: queryKeys.members.all`. Scattering string keys through
 * feature files is how a cache invalidation quietly stops matching.
 */
export const queryKeys = {
  analytics: ['analytics'] as const,

  applications: {
    all: ['applications'] as const,
    list: (filters: Record<string, unknown>) => ['applications', 'list', filters] as const,
    detail: (id: string) => ['applications', 'detail', id] as const,
  },

  moderation: {
    all: ['moderation'] as const,
    list: (filters: Record<string, unknown>) => ['moderation', 'list', filters] as const,
  },

  deletions: {
    all: ['deletions'] as const,
    list: (filters: Record<string, unknown>) => ['deletions', 'list', filters] as const,
  },

  users: {
    all: ['users'] as const,
    list: (filters: Record<string, unknown>) => ['users', 'list', filters] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
  },

  staff: {
    all: ['staff'] as const,
    list: () => ['staff', 'list'] as const,
  },

  subscriptions: {
    all: ['subscriptions'] as const,
    list: (filters: Record<string, unknown>) => ['subscriptions', 'list', filters] as const,
  },

  plans: {
    all: ['plans'] as const,
    list: () => ['plans', 'list'] as const,
  },

  coupons: {
    all: ['coupons'] as const,
    list: () => ['coupons', 'list'] as const,
  },

  payments: {
    all: ['payments'] as const,
    list: (filters: Record<string, unknown>) => ['payments', 'list', filters] as const,
  },

  /** Sect.fit memberships (Advanced / Pro) - separate from trainer-plan `subscriptions`. */
  memberships: {
    all: ['memberships'] as const,
    list: (filters: Record<string, unknown>) => ['memberships', 'list', filters] as const,
    detail: (id: string) => ['memberships', 'detail', id] as const,
    catalogue: ['memberships', 'catalogue'] as const,
    assignments: (filters: Record<string, unknown>) => ['memberships', 'assignments', filters] as const,
    anomalies: (filters: Record<string, unknown>) => ['memberships', 'anomalies', filters] as const,
  },

  /** Sect Coin wallets and the economy settings - `features/wallet`. */
  wallet: {
    all: ['wallet'] as const,
    overview: ['wallet', 'overview'] as const,
    economy: ['wallet', 'economy'] as const,
    wallets: ['wallet', 'wallets'] as const,
    transactions: ['wallet', 'transactions'] as const,
    member: (uid: string) => ['wallet', 'member', uid] as const,
    memberTransactions: (uid: string) => ['wallet', 'member', uid, 'transactions'] as const,
  },

  tickets: {
    all: ['tickets'] as const,
    list: (filters: Record<string, unknown>) => ['tickets', 'list', filters] as const,
    detail: (id: string) => ['tickets', 'detail', id] as const,
  },

  messages: {
    all: ['messages'] as const,
    channels: ['messages', 'channels'] as const,
    list: (filters: Record<string, unknown>) => ['messages', 'list', filters] as const,
    preview: (audience: Record<string, unknown>) => ['messages', 'preview', audience] as const,
  },

  auditLogs: {
    all: ['audit-logs'] as const,
    list: (filters: Record<string, unknown>) => ['audit-logs', 'list', filters] as const,
  },

  config: ['config'] as const,

  certificateTypes: ['certificateTypes'] as const,

  skills: ['skills'] as const,
} as const;
